import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function PublicBanner() {
  const { bannerId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadBanner();
  }, [bannerId]);

  const loadBanner = async () => {
    try {
      const response = await axios.get(`${API}/public/banner/${bannerId}`);
      setData(response.data);
    } catch (error) {
      setError("Banner not found");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#020617] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#10B981]"></div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-[#020617] flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-white mb-2">Banner Not Found</h1>
          <p className="text-gray-400">The banner you're looking for doesn't exist.</p>
        </div>
      </div>
    );
  }

  const { banner, lottery_result, assets, number_set } = data;
  const numbers = lottery_result.numbers || [0, 0, 0, 0, 0, 0];
  const lotteryResultText = lottery_result.result_text || "";

  const renderNumbers = () => {
    if (!banner.use_number_images) {
      // Render as text
      const textStyle = {
        fontFamily: banner.number_text_font || "Arial",
        color: banner.number_text_color || "#FFFFFF",
        fontSize: `${banner.number_text_size || 24}px`,
        textShadow: banner.number_text_shadow ? '2px 2px 4px rgba(0,0,0,0.8)' : 'none',
      };
      
      return numbers.map((num, index) => (
        <span key={`num-${index}-${num}`} style={textStyle} className="font-bold px-2">
          {num}
        </span>
      ));
    }
    
    // Render as images
    let numberAssets = assets;
    if (number_set && number_set.numbers) {
      numberAssets = number_set.numbers;
    }
    
    return numbers.map((num, index) => {
      const imageData = numberAssets[num.toString()] || numberAssets[num];
      return (
        <img
          key={`img-${index}-${num}`}
          src={imageData || `https://via.placeholder.com/60x60/10B981/020617?text=${num}`}
          alt={`Number ${num}`}
          className="w-12 h-12 object-contain"
        />
      );
    });
  };

  // Generate shadow style helper
  const getShadowStyle = (shadow, shadowX, shadowY, shadowBlur, shadowColor) => {
    if (!shadow) return 'none';
    const x = shadowX || 2;
    const y = shadowY || 2;
    const blur = shadowBlur || 4;
    const color = shadowColor || '#000000';
    return `${x}px ${y}px ${blur}px ${color}`;
  };

  return (
    <div className="w-full h-full flex items-center justify-center bg-transparent p-4">
      <div
        className="relative rounded-lg overflow-hidden shadow-2xl"
        style={{
          width: `${banner.banner_width || 800}px`,
          height: `${banner.banner_height || 400}px`,
          maxWidth: "100%",
          backgroundImage: `url(${banner.background_image})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
        data-testid="public-banner"
      >
        {/* Result Text (from lottery) */}
        {(banner.show_result_text !== false) && lotteryResultText && (
          <div
            className="absolute"
            style={{
              left: `${banner.result_text_position_x || 50}%`,
              top: `${banner.result_text_position_y || 10}%`,
              fontFamily: banner.result_text_font || "Arial",
              color: banner.result_text_color || "#FFFFFF",
              fontSize: `${banner.result_text_size || 18}px`,
              fontWeight: banner.result_text_font_weight || 700,
              textShadow: getShadowStyle(
                banner.result_text_shadow !== false,
                banner.result_text_shadow_x,
                banner.result_text_shadow_y,
                banner.result_text_shadow_blur,
                banner.result_text_shadow_color
              ),
            }}
            data-testid="banner-result-text"
          >
            {lotteryResultText}
          </div>
        )}

        {/* Custom Text */}
        {(banner.show_custom_text !== false && banner.show_text !== false) && banner.custom_text && (
          <div
            className="absolute"
            style={{
              left: `${banner.text_position_x}%`,
              top: `${banner.text_position_y}%`,
              fontFamily: banner.text_font || "Arial",
              color: banner.text_color || "#FFFFFF",
              fontSize: `${banner.text_size || 20}px`,
              fontWeight: banner.text_font_weight || 700,
              textShadow: getShadowStyle(
                banner.text_shadow !== false,
                banner.text_shadow_x,
                banner.text_shadow_y,
                banner.text_shadow_blur,
                banner.text_shadow_color
              ),
            }}
            data-testid="banner-text"
          >
            {banner.custom_text}
          </div>
        )}

        <div
          className="absolute flex items-center"
          style={{
            left: `${banner.position_x}%`,
            top: `${banner.position_y}%`,
            transform: `scale(${banner.scale})`,
            gap: `${banner.number_gap || 2}px`,
          }}
          data-testid="banner-numbers"
        >
          {renderNumbers()}
        </div>
      </div>
    </div>
  );
}